import React, { useMemo, useState } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { ContentMetricType } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { useContentService } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useContentService';
import { ContentSplineChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentSplineChart';
import { ContentTableGrid } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentTableGrid';
import { KeywordsGaugeCards } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/KeywordsGaugeCards';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { KeywordFilter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import {
  KEYWORD_FILTER_TO_METRIC_TYPE,
  KeywordBreakdownData
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import { useMainEntityType, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { ProductLevelKeywordPerformanceDetails } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ProductLevelKeywordPerformanceDetails';
import { useContentChartSubtitles } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useDynamicSubtitle';
import { formatMetric } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  createMetricColumnDefinition,
  createProductColumn
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/utils';
import colors from 'src/utils/colors';

/**
 * Calculates aggregate keyword metrics from section keyword data
 *
 * @param data - Array of SectionKeywords containing content phrases and their metadata
 * @returns Object containing:
 *  - productiveKeywords: Total number of keywords with positive weight
 *  - unproductiveKeywords: Total number of keywords with zero or negative weight
 *  - totalHits: Sum of all keyword hits across all sections
 */

const getKeywordMetricsForSubtitle = (data: KeywordBreakdownData) => {
  const { sections, summaryScores } = data;
  const uniquePhrases = sections.reduce((acc, section) => {
    section.content.forEach((phrase) => {
      // eslint-disable-next-line no-unused-expressions
      phrase?.metadata?.weights?.forEach((keywordWeight) => {
        acc.add(keywordWeight.keyword);
      });
    });

    return acc;
  }, new Set<string>());
  const uniquePhraseCount = uniquePhrases.size;

  const result = sections
    .map((section) => {
      const { content } = section;
      return content.reduce(
        (acc, phrase) => {
          const { metadata } = phrase;
          if (!metadata) {
            return acc;
          }

          const { totalWeight, weights: keywordWeights } = metadata;
          if (!keywordWeights) {
            if (totalWeight > 0) {
              return {
                ...acc,
                productiveKeywords: acc.productiveKeywords + 1,
                totalHits: acc.totalHits + totalWeight
              };
            } else {
              return {
                ...acc,
                unproductiveKeywords: acc.unproductiveKeywords + 1,
                totalHits: acc.totalHits + totalWeight
              };
            }
          }

          const productiveKeywords = keywordWeights.filter((keywordWeight) => keywordWeight.weight > 0).length;
          const unproductiveKeywords = keywordWeights.filter((keywordWeight) => keywordWeight.weight <= 0).length;
          return {
            productiveKeywords: acc.productiveKeywords + productiveKeywords,
            unproductiveKeywords: acc.unproductiveKeywords + unproductiveKeywords,
            totalHits: acc.totalHits + totalWeight
          };
        },
        { productiveKeywords: 0, unproductiveKeywords: 0, totalHits: 0 }
      );
    })
    .reduce(
      (acc, curr) => {
        return {
          productiveKeywords: acc.productiveKeywords + curr.productiveKeywords,
          unproductiveKeywords: acc.unproductiveKeywords + curr.unproductiveKeywords,
          totalHits: acc.totalHits + curr.totalHits
        };
      },
      { productiveKeywords: 0, unproductiveKeywords: 0, totalHits: 0 }
    );

  return {
    ...result,
    totalKeywords: summaryScores.averageCountOfKeywords,
    numProductiveKeywords: summaryScores.averageCountOfKeywordsGeneratingTraffic,
    numUnproductiveKeywords:
      summaryScores.averageCountOfKeywords - summaryScores.averageCountOfKeywordsGeneratingTraffic,
    totalSearchCount: summaryScores.averageVolumeOfSearchTraffic,
    totalUniqueKeywords: uniquePhraseCount
  };
};

const ContentKeywordsPage = () => {
  const {
    getContentScoresByWeek,
    getContentScoresByProduct,
    getKeywordPerformanceScoreSummary,
    getProductLevelKeywordPerformanceDetails
  } = useContentService();

  const { generateKeywordPerformanceSubtitle, generateSplineChartSubtitle, generateTableGridSubtitle } =
    useContentChartSubtitles();

  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const entityType = useMainEntityType();
  const searchParams = new URLSearchParams(window.location.search);
  const initialKeywordFilter = (searchParams.get('keywordFilter') as KeywordFilter) || KeywordFilter.Usage;
  const [keywordFilter, setKeywordFilter] = useState<KeywordFilter>(initialKeywordFilter);

  const columnDefinitions = useMemo(() => {
    const defaultColumnDefinitions = [
      ['keywordUsageScore', 'Keyword Usage'],
      ['keywordProductivityScore', 'Keyword Productivity'],
      ['keywordPopularityScore', 'Keyword Popularity']
    ].map(([id, header]) => createMetricColumnDefinition([id, header]));

    // Map of filter to column definition
    const filterToColumn = {
      [KeywordFilter.Usage]: createMetricColumnDefinition(['keywordUsageScore', 'Keyword Usage']),
      [KeywordFilter.Productivity]: createMetricColumnDefinition(['keywordProductivityScore', 'Keyword Productivity']),
      [KeywordFilter.Popularity]: createMetricColumnDefinition(['keywordPopularityScore', 'Keyword Popularity'])
    };

    // Return selected column first, followed by remaining columns (excluding selected column)
    const selectedColumn = filterToColumn[keywordFilter];
    return [selectedColumn, ...defaultColumnDefinitions.filter((col) => col.id !== selectedColumn.id)];
  }, [keywordFilter]);

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: `${BEACON_CHART_SPACING}px` }}>
      <KeywordsGaugeCards
        title="Keyword Performance"
        queryFn={() => getKeywordPerformanceScoreSummary(mainTimePeriod, comparisonTimePeriod)}
        selectedFilter={keywordFilter}
        onClickValue={setKeywordFilter}
        renderSubtitle={() => {
          return generateKeywordPerformanceSubtitle();
        }}
      />

      <ContentSplineChart
        title={`Keyword ${keywordFilter}`}
        queryFn={() =>
          getContentScoresByWeek(KEYWORD_FILTER_TO_METRIC_TYPE[keywordFilter], mainTimePeriod, comparisonTimePeriod)
        }
        renderSubtitle={(score: string, change: number) => {
          return generateSplineChartSubtitle({
            formattedAverage: score,
            metricName: `keyword ${keywordFilter.toLowerCase()} score`,
            formattedPercentChange: formatMetric(Math.abs(change), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
            direction: change > 0 ? 'higher' : 'lower'
          });
        }}
      />

      {entityType === 'product' && (
        <ProductLevelKeywordPerformanceDetails
          queryFn={() => getProductLevelKeywordPerformanceDetails(keywordFilter, mainTimePeriod)}
          keywordFilter={keywordFilter}
          renderSubtitle={(data: KeywordBreakdownData) => {
            const {
              totalSearchCount,
              totalKeywords,
              numProductiveKeywords,
              numUnproductiveKeywords,
              totalUniqueKeywords
            } = getKeywordMetricsForSubtitle(data);
            const formatKeywordsCount = (value: number) =>
              formatMetric(value, METRICTYPE.INTEGER, { showFullValue: true, decimalPlaces: 0 });

            const formattedTotalSearchCount = formatMetric(totalSearchCount, METRICTYPE.VOLUME, {
              showFullValue: false
            });
            const formattedProductiveKeywords = formatKeywordsCount(numProductiveKeywords);
            const formattedUnproductiveKeywords = formatKeywordsCount(numUnproductiveKeywords);
            const formattedTotalKeywords = formatKeywordsCount(totalKeywords);
            const formattedUniqueKeywords = formatKeywordsCount(totalUniqueKeywords);

            switch (keywordFilter) {
              case KeywordFilter.Usage:
                return `The section below displays this product's current content. Keyword phrases and individual keywords found in the content are highlighted with organic traffic value. There are a total of ${formattedTotalKeywords} distinct keywords found in the content.`;
              case KeywordFilter.Productivity:
                return `The section below displays this product's current content. Keyword phrases and individual keywords that are driving organic traffic to the product page are highlighted in green. Unproductive phrases and keywords that are not driving organic traffic to the product page are highlighted in red. There are a total of ${formattedProductiveKeywords} distinct productive keywords and ${formattedUnproductiveKeywords} distinct unproductive keywords in the content.`;
              case KeywordFilter.Popularity:
                return `The section below displays this product's current content. Keyword phrases and individual keywords found in the content are highlighted with search volume. There are a total of ${formattedUniqueKeywords} keywords in the content driving a total of ${formattedTotalSearchCount} searches to this product.`;
              default:
                return '';
            }
          }}
        />
      )}

      {entityType !== 'product' && (
        <ContentTableGrid
          // This is a workaround to avoid re-querying the data due to the filter updating the chart title, which is used to generate the query key
          queryKeyOverride="content-table-grid-keyword-performance-by-product"
          title={`Keyword ${keywordFilter} by Product`}
          productColumn={createProductColumn('562px')}
          columnDefinitions={columnDefinitions}
          queryFn={() =>
            getContentScoresByProduct(ContentMetricType.KeywordPerformanceScore, mainTimePeriod, comparisonTimePeriod)
          }
          tableSx={{
            minWidth: 650,
            borderCollapse: 'unset',
            '& tbody .MuiTableCell-root': {
              padding: '12px 72px',
              height: '60px',
              paddingRight: '0px'
            },
            '& thead .MuiTableCell-root': {
              padding: '0px 0px 16px 72px'
            },
            '& tbody td': {
              borderBottom: '1px solid #f0f0f0' // Move border bottom here
            },
            '& thead th': {
              border: 'none' // Explicitly remove borders from header cells
            },
            '& thead th:first-of-type': {
              padding: '0px 0px 16px 16px'
            },
            '& tbody td:first-of-type': {
              borderLeft: '1px solid #f0f0f0',
              padding: '16px 0px 16px 16px'
            },
            '& tbody td:last-child': {
              borderRight: '1px solid #f0f0f0'
            },
            '& tbody tr:first-of-type td': {
              borderTop: '1px solid #f0f0f0'
            },
            '& tbody tr:first-of-type td:first-of-type': {
              borderTopLeftRadius: '8px'
            },
            '& tbody tr:first-of-type td:last-of-type': {
              borderTopRightRadius: '8px'
            },
            '& tbody tr:first-of-type td:last-child': {
              borderTopRightRadius: '8px'
            },
            '& tbody tr:last-child td:first-of-type': {
              borderBottomLeftRadius: '8px'
            },
            '& tbody tr:last-child td:last-child': {
              borderBottomRightRadius: '8px'
            },
            '& thead .MuiTableCell-root span': {
              fontFamily: 'Roboto',
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: '20px',
              letterSpacing: '1.2px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              color: colors.darkBlue,
              textTransform: 'uppercase'
            }
          }}
          renderSubtitle={(view) => {
            return generateTableGridSubtitle({
              view,
              metricName: `keyword ${keywordFilter.toLowerCase()} score`
            });
          }}
        />
      )}
    </BasePageContainer>
  );
};

export default ContentKeywordsPage;
